// store.js
import { defineStore } from "pinia";
import Cookies from "js-cookie";
import { authCookieName } from "./constants";

const getDefaultState = () => {
	return {
		authState: Cookies.get(authCookieName),
		dialogExpanded: false,
		dirty: false,
		discardSde: false,
		done: false,
		exit: false,
		fetchMetadata: false,
		help: "",
		// DCTL-2184: when an admin is impersonating ("view as"), the admin's
		// identity { uuid, email, first_name, last_name }; null otherwise. NOT
		// persisted (see the `pick` list) - it is session-derived, re-read from
		// /users/me on every bootstrap so a reload never shows a stale banner.
		impersonator: null,
		isAdmin: false,
		// DCTL-2481: whether to OFFER the internal reviewer XLSX format in the QC
		// Review export menu. Role-derived, so it can only come from the server
		// (the store tracks permissions, not roles) - GET
		// /quality-control/:uuid/export-xlsx returns it on the permissions
		// payload beside isProspect. It decides what is offered, nothing more:
		// the endpoint is the enforcement point and refuses a caller without the
		// role with a 403.
		isInternalXlsxReviewer: false,
		// DCTL-2046/2047: whether to SHOW the lead-only isUrgent /
		// isTrainingMaterial project toggles (administrator or group_lead).
		// Role-derived like isInternalXlsxReviewer, from the same permissions
		// payload; ProjectsController is the enforcement point.
		canManageSpecialProjectFlags: false,
		isProspect: false,
		module: "wpd",
		notification: {
			message: "",
			status: "",
			persist: false,
			// timeout + forceClear were written by the notification mutations but
			// never declared in the old Vuex default state (DCTL-1737). Pinia
			// options stores must declare every reactive field so $reset() and the
			// persistence `pick` track them, so they are declared here.
			timeout: 3000,
			forceClear: false,
		},
		paper: null,
		permissions: {},
		// uuid of the user the `permissions` map + `isAdmin` belong to. Guards
		// against a previous session's permissions (notably an admin's all-true
		// map) leaking into the next user: the bootstrap re-fetches whenever this
		// does not match the current user. Persisted alongside `permissions` so a
		// same-user reload still skips the fetch.
		permissionsUserUuid: null,
		qcPaper: null,
		qcSave: false,
		// DCTL-2682: uuid of the QC whose /finalize POST is in flight, so only
		// that QC's finalize buttons are held. Not persisted: a reload
		// mid-finalize must not leave them disabled.
		qcFinalizingUuid: null,
		qcSaveUUID: null,
		qcAllStepsCompleted: false,
		qcReadOnly: false,
		resetDataEntry: false,
		isReworkSde: false,
		// holds a pending save promise resolver (sendSave -> receiveSave);
		// likewise undeclared in the old Vuex default state.
		resolve: null,
		save: false,
		saveInProgress: false,
		saveUUID: null,
		saveSde: false,
		sdeComplete: false,
		sessionProjects: [],
		sessionOrganizations: [],
		user: {
			id: -1,
			cookie_notice_dismissed_at: null,
			prefers_legacy_queue: false,
			email: "",
			first_name: "",
			last_name: "",
			initials: "",
			uuid: "",
			sso_enabled: false,
		},
		validate: false,
	};
};

export const useMainStore = defineStore("main", {
	state: getDefaultState,
	// vuex-persistedstate's `paths` becomes pinia-plugin-persistedstate's `pick`
	// (same dot-notation). The localStorage key is kept as "vuex" so existing
	// signed-in sessions hydrate seamlessly across the deploy (auth itself rides
	// a cookie, not persisted state).
	persist: {
		key: "vuex",
		pick: [
			"dialogExpanded",
			"isAdmin",
			"isInternalXlsxReviewer",
			"canManageSpecialProjectFlags",
			"isProspect",
			"paper",
			"permissions",
			"permissionsUserUuid",
			"qcPaper",
			"qcSaveUUID",
			"saveUUID",
			"sessionProjects",
			"sessionOrganizations",
			"user.id",
			"user.email",
			"user.first_name",
			"user.last_name",
			"user.initials",
			"user.uuid",
			"user.cookie_notice_dismissed_at",
			"user.prefers_legacy_queue",
			"user.sso_enabled",
		],
	},
	getters: {
		can: (state) => (permission) =>
			state.isAdmin || state.permissions[permission] || false,
		canAll: (state) => (permissions) =>
			state.isAdmin || permissions.every((p) => state.permissions[p]),
		canAny: (state) => (permissions) =>
			state.isAdmin || permissions.some((p) => state.permissions[p]),
		isAuthenticated: (state) => Boolean(state.authState),
		isQcFinalizing: (state) => (qcUuid) =>
			Boolean(qcUuid) && state.qcFinalizingUuid === qcUuid,
		// DCTL-2184: true while an admin is viewing the app as another user.
		isImpersonating: (state) => Boolean(state.impersonator),
		userPermissions: (state) => state.permissions,
	},
	actions: {
		reset() {
			this.$reset();
		},
		showNotification(payload) {
			this.notification.message = payload.message;
			this.notification.status = payload.status;
			this.notification.persist = payload.persist;
			this.notification.timeout = payload.timeout;
			this.notification.forceClear = false;
		},
		clearNotification(payload) {
			this.notification.message = "";
			this.notification.status = "";
			this.notification.persist = false;
			this.notification.timeout = 3000;
			this.notification.forceClear = payload?.force ?? false;
		},
		setAuthenticated(value) {
			this.authState = value;
		},
		setUser(payload) {
			this.user.id = payload.id;
			this.user.uuid = payload.uuid;
			this.user.email = payload.email;
			this.user.first_name = payload.first_name;
			this.user.last_name = payload.last_name;
			this.user.initials =
				payload.first_name.charAt(0) + payload.last_name.charAt(0);
			this.user.cookie_notice_dismissed_at = payload.cookie_notice_dismissed_at;
			// DCTL-2113: project-dashboard rollout escape-hatch preference. Coerce
			// so a missing field on an older payload lands as false, not undefined.
			this.user.prefers_legacy_queue = Boolean(payload.prefers_legacy_queue);
			this.user.sso_enabled = payload.sso_enabled;
			// DCTL-2184: the /users/me rehydrate (main.js) and the impersonation
			// start/stop handlers pass the impersonator breadcrumb alongside the
			// user; set it from there. Only touch it when the key is present so a
			// bare-user setUser (fresh login, cookie-dismiss record-action) can
			// never clear an active impersonation.
			if ("impersonator" in payload) {
				this.impersonator = payload.impersonator ?? null;
			}
		},
		// DCTL-2113: optimistic setter for the classic-queue opt-out so the UI
		// re-renders old<->new immediately; the PATCH response then rehydrates the
		// full user via setUser.
		setPrefersLegacyQueue(value) {
			this.user.prefers_legacy_queue = Boolean(value);
		},
		clearUser() {
			this.user = getDefaultState().user;
		},
		setPermissions(payload) {
			this.isAdmin = payload.isAdmin;
			this.isInternalXlsxReviewer = payload.isInternalXlsxReviewer ?? false;
			this.canManageSpecialProjectFlags =
				payload.canManageSpecialProjectFlags ?? false;
			this.isProspect = payload.isProspect ?? false;
			this.permissions = payload.permissions;
			// Stamp the owning user so the bootstrap can tell whether the loaded
			// permissions still belong to the current user (defaults to null when
			// omitted, forcing a re-fetch).
			this.permissionsUserUuid = payload.userUuid ?? null;
		},
		clearPermissions() {
			this.isAdmin = false;
			this.isInternalXlsxReviewer = false;
			this.canManageSpecialProjectFlags = false;
			this.isProspect = false;
			this.permissions = {};
			this.permissionsUserUuid = null;
		},
		setDialogExpanded(payload) {
			this.dialogExpanded = payload;
		},
		setPaper(payload) {
			this.paper = payload;
		},
		clearPaper() {
			this.paper = null;
		},
		setQCPaper(payload) {
			this.qcPaper = payload;
		},
		clearQCPaper() {
			this.qcPaper = null;
		},
		sendSave(payload) {
			if (payload) {
				if (payload.type === "reset_data_entry") {
					this.resetDataEntry = true;
				} else if (payload.type === "discard_sde") {
					this.discardSde = true;
				} else if (payload.type === "data_entry") {
					this.save = true;
				} else if (payload.type === "sde") {
					this.saveSde = true;
				} else if (payload.type === "quality_control") {
					this.qcSave = true;
				}

				if (payload.done) {
					this.done = true;
				}
				if (payload.exit) {
					this.exit = true;
				}
				if (payload.resolve) {
					this.resolve = payload.resolve;
				}
			}
		},
		receiveSave() {
			this.discardSde = false;
			this.resetDataEntry = false;
			this.saveInProgress = false;
			this.save = false;
			this.saveSde = false;
			this.qcSave = false;
			this.done = false;
			this.exit = false;

			if (this.resolve) {
				this.resolve();
				this.resolve = null;
			}
		},
		sendValidate() {
			this.validate = true;
		},
		receiveValidate() {
			this.validate = false;
		},
		sendSdeComplete() {
			this.sdeComplete = true;
		},
		receiveSdeComplete() {
			this.sdeComplete = false;
		},
		sendFetchMetadata() {
			this.fetchMetadata = true;
		},
		receiveFetchMetadata() {
			this.fetchMetadata = false;
		},
		setDirty() {
			this.dirty = true;
		},
		clearDirty() {
			this.dirty = false;
		},
		setSaveInProgress(payload) {
			this.saveInProgress = payload;
		},
		setIsReworkSde(payload) {
			this.isReworkSde = !!payload;
		},
		setSaveUUID(payload) {
			this.saveUUID = payload;
		},
		clearSaveUUID() {
			this.saveUUID = null;
		},
		setQCSaveUUID(payload) {
			this.qcSaveUUID = payload;
		},
		clearQCSaveUUID() {
			this.qcSaveUUID = null;
		},
		setQcAllStepsCompleted(payload) {
			this.qcAllStepsCompleted = payload;
		},
		setQcReadOnly(payload) {
			this.qcReadOnly = !!payload;
		},
		setQcFinalizingUuid(qcUuid) {
			this.qcFinalizingUuid = qcUuid;
		},
		// Clears only the caller's own QC: a finalize that settles after the
		// reviewer has started one on another QC must not release that one.
		clearQcFinalizingUuid(qcUuid) {
			if (this.qcFinalizingUuid === qcUuid) this.qcFinalizingUuid = null;
		},
		setSessionProjects(payload) {
			this.sessionProjects = payload;
		},
		clearSessionProjects() {
			this.sessionProjects = [];
		},
		setSessionOrganizations(payload) {
			this.sessionOrganizations = payload;
		},
		clearSessionOrganizations() {
			this.sessionOrganizations = [];
		},
		setHelp(payload) {
			this.help = payload;
		},
		clearHelp() {
			this.help = "";
		},
	},
});
